import { Link, useParams } from "react-router-dom";
import AuthLayout from "../../../components/layouts/auth"
import { useSingleBusinessMerchant } from "../../../composables/business-merchant/useSingleBusinessMerchant"
import LineLoader from "../../../components/loader/line-loader";
import { ChevronLeft } from "lucide-react";
import BusinessMerchantProps from "../../../types/business-merchant-type";
import { BusinessVerifiedStatusBadge } from "../../../components/ui/BusinessMerchant";
import moment from "moment";
import { numberWithCommas, strReplace, ucFirst } from "../../../lib/helper-support";
import Button from "../../../components/form/button";
import DefaultModal from "../../../components/modal/default-modal";
import DataLabel from "../../../components/ui/DataLabel";
import { useApi } from "../../../providers/ApiProvider";

const SingleBusinessMerchant = () => {
    const { code } = useParams<{ code: string }>();
    const { data, isLoading }: { data?: BusinessMerchantProps; isLoading: boolean } = useSingleBusinessMerchant({
        code: code || '',
        enabled: !!code,
    });

    const api = useApi();

    const toggleVerified = async () => {
        api.POST({
            endpoint: `business-merchant/${code}/toggle-verified`,
            refreshKeys: ['business-merchant', code],
        });
    }

    return (
        <AuthLayout>
            {isLoading && <LineLoader />}
            {!isLoading && data && (
                <div>
                    <h2 className="text-[#8D8D8D] font-[600] text-[14px]">Business Merchant / details</h2>
                    <div className="flex items-center pl-0 mt-4 mb-5 space-x-2">
                        <span>
                            <Link to={'/business-merchants'}><ChevronLeft size={20} className="-ml-2" /></Link>
                        </span>
                        <h1 className="font-[600] text-tertiary text-[18px]">
                            Merchant Code: {data.code}
                        </h1>
                    </div>
                    <div className="flex gap-8 flex-wrap mb-5">
                        <div>
                            <BusinessVerifiedStatusBadge verified={data.is_verified} />
                        </div>
                        <div className="themeTextMuted flex gap-1 flex-col">
                            <label>Account created:</label>
                            <span className="text-black font-bold">{moment(data.created_at).format('MMM D, YYYY')}</span>
                        </div>
                        <div className="themeTextMuted flex gap-1 flex-col">
                            <label>Verified at:</label>
                            <span className="text-black font-bold">{data.verified_at ? moment(data.verified_at).format('MMM D, YYYY') : '-'}</span>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link to={`/business-merchants/${data.code}/documentation`}>
                                <Button design="primary-outline" text="Documentations" />
                            </Link>
                            {!data.is_verified && (
                                <DefaultModal
                                    onConfirmationAction={toggleVerified}
                                    modalButtonTriggerText={<Button text="Mark as Verified" />}
                                    description="Are you sure you want to mark this merchant as verified?"
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex md:flex-row flex-col-reverse md:gap-20 gap-5">
                        <div className="grid md:grid-cols-2 gap-4 md:w-3/5 h-fit">
                            <DataLabel label="Business Name" value={ucFirst(data.business_name)} />
                            <DataLabel label="Ctag" value={data.ctag} />
                            <DataLabel label="Registration Email" value={data.email} />
                            <DataLabel label="Registration Phone Number" value={data.phone_number || '-'} />
                            <DataLabel label="Is Business Registered" value={data.is_registered ? 'Yes' : 'No'} />
                            <DataLabel label="Type of Business" value={<span className="capitalize">{strReplace(data.business_information?.type_of_business || '-', '_', ' ')}</span>} />
                            <DataLabel label="Nature of Business" value={data.business_information?.nature_of_business || '-'} />
                            <DataLabel label="Govt. Number" value={data.business_information?.rc_number || '-'} />
                            <DataLabel label="Business Activity" value={data.business_information?.description_of_business_activity || '-'} />
                            <DataLabel label="Business Email" value={data.business_information?.business_email || '-'} />
                            <DataLabel label="Phone Number" value={data.business_information?.business_phone_number || '-'} />
                            <DataLabel label="Address" value={data.business_information?.business_address || '-'} />
                        </div>
                        <div className="md:w-2/5 grid gap-4 h-fit">
                            <DataLabel label="Account Balance" value={<span>{data.wallet_currency} {numberWithCommas(data.wallet_balance, true)}</span>} />
                            <div className="flex flex-wrap gap-4">
                                <DataLabel label="Total Customers" value={<span>{numberWithCommas(data.total_customers)}</span>} />
                                <DataLabel label="Transaction Count" value={<span>{numberWithCommas(data.transaction_count)}</span>} />
                                <DataLabel label="Transaction Volume" value={<span>{numberWithCommas(data.transaction_amount, true)}</span>} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!isLoading && !data && (
                <div>
                    <h1 className="text-[2em] font-[600] text-primary">Business Merchant Not Found</h1>
                    <Link to={'/business-merchants'} className="flex items-center mt-5 space-x-2 font-[400] text-gray-500 hover:underline">
                        <ChevronLeft size={13} />
                        <span>back to business merchants</span>
                    </Link>
                </div>
            )}
        </AuthLayout>
    )
}

export default SingleBusinessMerchant